import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import ModernDashboardHeader from '../../components/ModernDashboardHeader/ModernDashboardHeader';
import KpiCard from '../../components/KPICard/KPICard';
import { fetchComplaints, ComplaintListItem, fetchAuthorizationTypes } from './services/complaintsAPI';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import styles from './ComplaintsPage.module.css';
import CustomSelect from '../../components/CustomSelect';
import AddIcon from '@mui/icons-material/Add';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import BlockIcon from '@mui/icons-material/Block';
import { DateNavigator, formatLocalDate, getYearRange, getMonthRange, getWeekRange } from '../../components/DateNavigator/DateNavigator';
import navStyles from '../../components/DateNavigator/DateNavigator.module.css';
import NewComplaintModal from './components/NewComplaintModal';
import ComplaintDetailView from './components/ComplaintDetailView';
import OrderDetailModal from '../orders/components/Pedidos/OrderDetailModal';
import ComplaintsPageSkeleton from './ComplaintsPageSkeleton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { formatDateSafely } from './utils/formatters';


const ITEMS_PER_PAGE = 20;

export const formatProtocol = (protocol: string | undefined | null) => {
    if (!protocol) return '-';
    // Se o protocolo possui 3 partes separadas por ponto (NN.MM.YY)
    const parts = protocol.split('.');
    if (parts.length === 3) {
        return `${parts[0]}.${parts[1]}`; // Exibe apenas NN.MM
    }
    return protocol;
};

const ComplaintsPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [complaints, setComplaints] = useState<ComplaintListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [isNewModalOpen, setIsNewModalOpen] = useState(false);
    const [selectedComplaintId, setSelectedComplaintId] = useState<number | null>(null);
    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
    const [focusReposicaoOnOpen, setFocusReposicaoOnOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    
    // Local date navigator state
    const [periodo, setPeriodo] = useState<'anual' | 'mensal' | 'semanal' | 'custom'>('anual');
    const [dataRef, setDataRef] = useState<string>(() => formatLocalDate(new Date()));
    const [dataInicio, setDataInicio] = useState<string>('');
    const [dataFim, setDataFim] = useState<string>('');

    const { startDate, endDate } = React.useMemo(() => {
        if (periodo === 'custom') {
            return { startDate: dataInicio, endDate: dataFim };
        }
        const dRef = dataRef || formatLocalDate(new Date());
        let range;
        if (periodo === 'mensal') {
            range = getMonthRange(dRef);
        } else if (periodo === 'semanal') {
            range = getWeekRange(dRef);
        } else { // default to 'anual'
            range = getYearRange(dRef);
        }
        return {
            startDate: formatLocalDate(range.start),
            endDate: formatLocalDate(range.end)
        };
    }, [periodo, dataRef, dataInicio, dataFim]);

    const [authTypeFilter, setAuthTypeFilter] = useState<string>('Todos');
    const [availableAuthTypes, setAvailableAuthTypes] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [counts, setCounts] = useState({ ABERTA: 0, EM_ANALISE: 0, CONCLUIDA: 0, PENDENTE: 0 });

    // Handle denouncing search input to avoid spamming the API
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(1); // Reset page on new search
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchComplaints(
                currentPage,
                ITEMS_PER_PAGE,
                statusFilter,
                debouncedSearch,
                startDate,
                endDate,
                authTypeFilter !== 'Todos' ? authTypeFilter : null
            );
            setComplaints(response.data ?? []);
            setTotalPages(response.pagination.totalPages);
            setTotalItems(response.pagination.total);
            if (response.counts) {
                setCounts(response.counts);
            }
        } catch (error) {
            console.error("Failed to load complaints", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, statusFilter, debouncedSearch, startDate, endDate, authTypeFilter]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Carregar tipos de autorização disponíveis
    useEffect(() => {
        const loadAuthTypes = async () => {
            try {
                const types = await fetchAuthorizationTypes();
                setAvailableAuthTypes(types);
            } catch (error) {
                console.error("Erro ao carregar tipos de autorização:", error);
            }
        };
        loadAuthTypes();
    }, []);

    // Inicializa a reclamação selecionada a partir da URL apenas na montagem do componente
    const didInitFromUrl = React.useRef(false);
    useEffect(() => {
        if (didInitFromUrl.current) return;
        didInitFromUrl.current = true;
        const idFromUrl = searchParams.get('id');
        if (idFromUrl) {
            const parsedId = parseInt(idFromUrl, 10);
            if (!isNaN(parsedId)) {
                setSelectedComplaintId(parsedId);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sincroniza estado → URL (uma única direção, sem loop)
    useEffect(() => {
        if (selectedComplaintId !== null) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('id', String(selectedComplaintId));
            setSearchParams(newParams, { replace: true });
        }
        // Limpeza da URL é feita exclusivamente pelo handleCloseDetailModal
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedComplaintId]);

    // Status mapping for badge styles
    const getStatusClass = (situacao: string) => {
        switch (situacao) {
            case 'ABERTA': return styles.statusOpen;
            case 'EM_ANALISE': return styles.statusAnalysis;
            case 'CONCLUIDA': return styles.statusCompleted;
            case 'PENDENTE': return styles.statusPending;
            case 'REJEITADA': return styles.statusRejected;
            default: return '';
        }
    };

    const getStatusLabel = (situacao: string) => {
        switch (situacao) {
            case 'ABERTA': return 'Aberta';
            case 'EM_ANALISE': return 'Em Análise';
            case 'CONCLUIDA': return 'Concluída';
            case 'PENDENTE': return 'Insira o valor da reposição';
            case 'REJEITADA': return 'Rejeitada';
            default: return situacao;
        }
    };

    const getRefundLabel = (type: string) => {
        switch (type) {
            case 'CREDITO': return 'Crédito';
            case 'DESCONTO': return 'Desconto';
            case 'REPOSICAO': return 'Reposição';
            case 'NENHUM': return 'Nenhum';
            default: return type || '-';
        }
    };

    const handleRowClick = (id: number) => {
        setFocusReposicaoOnOpen(false);
        setSelectedComplaintId(id);
    };

    const handleStatusCellClick = (id: number) => {
        setFocusReposicaoOnOpen(true);
        setSelectedComplaintId(id);
    };

    const handleCloseDetailModal = () => {
        setSelectedComplaintId(null);
        setFocusReposicaoOnOpen(false);
        // Clear search parameters
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('id');
        newParams.delete('tab');
        setSearchParams(newParams, { replace: true });
    };

    const handleCloseNewModal = () => {
        setIsNewModalOpen(false);
        // Recarregar dados ao fechar modal de criação
        if (currentPage === 1) {
            loadData();
        } else {
            setCurrentPage(1);
        }
    };

    const handleCloseOrderModal = () => {
        setSelectedOrderId(null);
    };

    const handleViewOrder = (orderId: number) => {
        setSelectedOrderId(orderId);
    };




    const clearFilters = () => {
        setSearchQuery('');
        setAuthTypeFilter('Todos');
        setStatusFilter(null);
        setPeriodo('anual');
        setDataRef(formatLocalDate(new Date()));
        setDataInicio('');
        setDataFim('');
        setCurrentPage(1);
    };

    // Contadores de KPIs vindos do backend
    const { ABERTA: abertas, EM_ANALISE: emAnalise, CONCLUIDA: concluidas, PENDENTE: pendentes } = counts;

    // Filtered Content is now handled server-side
    const displayComplaints = complaints;

    return (
        <div className={styles.pageContainer}>
            {selectedComplaintId !== null ? (
                /* Detail View (In-page) */
                <div className={styles.detailViewWrapper}>
                    <div className={styles.detailHeaderActions}>
                        <span className={styles.backLabel} onClick={handleCloseDetailModal}>
                            <ChevronLeftIcon fontSize="small" />
                            Voltar
                        </span>
                    </div>
                    <ComplaintDetailView
                        complaintId={selectedComplaintId}
                        onViewOrder={handleViewOrder}
                        autoFocusReposicao={focusReposicaoOnOpen}
                    />
                </div>
            ) : (
                /* Dashboard View */
                <>
                    {/* Filter Bar (Combo Panel: Controles + KPIs) */}
                    <div className={styles.filterBar}>
                        <div className={styles.filterTopRow}>
                            <div className={styles.filterGroup}>
                                <div className={navStyles.periodContainer}>
                                    {(['anual', 'mensal', 'semanal', 'custom'] as const).map(p => (
                                        <button
                                            key={p}
                                            onClick={() => {
                                                setPeriodo(p);
                                                setCurrentPage(1);
                                            }}
                                            className={`
                                                ${navStyles.periodButton}
                                                ${periodo === p ? navStyles.periodButtonActive : ''}
                                            `}
                                        >
                                            {p === 'custom' ? 'Personalizado' : p}
                                        </button>
                                    ))}
                                </div>

                                <DateNavigator
                                    label=""
                                    periodo={periodo}
                                    dataRef={dataRef}
                                    dataInicio={dataInicio}
                                    dataFim={dataFim}
                                    onChange={(vals) => {
                                        if (vals.periodo) setPeriodo(vals.periodo);
                                        if (vals.dataRef) setDataRef(vals.dataRef);
                                        if (vals.dataInicio) setDataInicio(vals.dataInicio);
                                        if (vals.dataFim) setDataFim(vals.dataFim);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                            <ModernDashboardHeader variant="minimal" />
                        </div>
                        <div className={styles.kpiBottomRow}>
                            <span className={styles.kpiMicroItem}>
                                <AssignmentLateOutlinedIcon style={{ fontSize: 16, color: '#f59e0b' }} />
                                <span className={styles.kpiMicroLabel}>Abertas:</span>
                                <span className={styles.kpiMicroValue}>{counts.ABERTA || 0}</span>
                            </span>
                            <span className={styles.kpiMicroItem}>
                                <HourglassEmptyOutlinedIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                                <span className={styles.kpiMicroLabel}>Em Análise:</span>
                                <span className={styles.kpiMicroValue}>{counts.EM_ANALISE || 0}</span>
                            </span>
                            <span className={styles.kpiMicroItem}>
                                <PendingOutlinedIcon style={{ fontSize: 16, color: '#8b5cf6' }} />
                                <span className={styles.kpiMicroLabel}>Pendentes:</span>
                                <span className={styles.kpiMicroValue}>{counts.PENDENTE || 0}</span>
                            </span>
                            <span className={styles.kpiMicroItem}>
                                <CheckCircleOutlinedIcon style={{ fontSize: 16, color: '#10b981' }} />
                                <span className={styles.kpiMicroLabel}>Concluídas:</span>
                                <span className={styles.kpiMicroValue}>{counts.CONCLUIDA || 0}</span>
                            </span>
                        </div>
                    </div>

                    <div className={styles.tableContainer}>
                                <div className={styles.tableToolbar}>
                                    <div className={styles.tableSearchBar}>
                                        <SearchIcon className={styles.tableSearchIcon} />
                                        <input
                                            type="text"
                                            className={styles.tableSearchInput}
                                            placeholder="Buscar por protocolo, cliente ou pedido..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        {searchQuery && (
                                            <ClearIcon
                                                className={styles.tableSearchClear}
                                                onClick={() => setSearchQuery('')}
                                            />
                                        )}
                                    </div>

                                    <div className={styles.tableFilters}>
                                        <CustomSelect
                                            id="status-select" label="Status"
                                            value={statusFilter || 'Todos'}
                                            onChange={(e) => {
                                                setStatusFilter(e.target.value === 'Todos' ? null : e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            placeholder="Status"
                                            options={[
                                                { value: 'Todos', label: 'Todos' },
                                                { value: 'ABERTA', label: 'Aberta' },
                                                { value: 'EM_ANALISE', label: 'Em Análise' },
                                                { value: 'PENDENTE', label: 'Pendente' },
                                                { value: 'CONCLUIDA', label: 'Concluída' }
                                            ]}
                                        />
                                        <CustomSelect
                                            id="decisao-select" label="Decisão"
                                            value={authTypeFilter}
                                            onChange={(e) => { setAuthTypeFilter(e.target.value); setCurrentPage(1); }}
                                            placeholder="Decisão"
                                            options={[
                                                { value: 'Todos', label: 'Todas' },
                                                ...(availableAuthTypes.length > 0
                                                    ? availableAuthTypes.map(type => ({ value: type, label: type === 'Autorizado' ? 'Reposição' : type }))
                                                    : [{ value: 'Autorizado', label: 'Reposição' }, { value: 'Pendente', label: 'Pendente' }]
                                                )
                                            ]}
                                        />
                                        {(statusFilter || searchQuery || authTypeFilter !== 'Todos') && (
                                            <button className={styles.clearFiltersBtn} onClick={clearFilters}>
                                                Limpar
                                            </button>
                                        )}
                                        <button className={styles.newComplaintButton} onClick={() => setIsNewModalOpen(true)}>
                                            <AddIcon fontSize="small" /> Abrir Reclamação
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.tableHeader}>
                                    <h2 className={styles.tableTitle}>Minhas Reclamações</h2>
                                </div>
                                {loading ? (
                                    <>
                                        <table className={styles.table}>
                                            <thead>
                                                <tr>
                                                    <th>Protocolo</th>
                                                    <th>Data</th>
                                                    <th>Cliente</th>
                                                    <th>Pedido</th>
                                                    <th>Decisão (CQ)</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                        </table>
                                        <ComplaintsPageSkeleton />
                                    </>
                                ) : (
                                    <>
                                        <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Protocolo</th>
                                            <th>Data</th>
                                            <th>Cliente</th>
                                            <th>Pedido</th>
                                            <th>Decisão (CQ)</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayComplaints.length === 0 ? (
                                            <tr><td colSpan={5}>
                                                <div className={styles.emptyState}>
                                                    <AssignmentLateOutlinedIcon style={{ fontSize: 48, color: '#e2e8f0', marginBottom: 16 }} />
                                                    <p>
                                                        {statusFilter
                                                            ? `Nenhuma reclamação encontrada com status "${getStatusLabel(statusFilter)}".`
                                                            : "Nenhuma reclamação registrada."}
                                                    </p>
                                                </div>
                                            </td></tr>
                                        ) : (
                                            displayComplaints.map(complaint => (
                                                <tr
                                                    key={complaint.id}
                                                    onClick={() => handleRowClick(complaint.id)}
                                                    className={styles.clickableRow}
                                                    title="Clique para ver detalhes"
                                                >
                                                    <td>
                                                        <div className={styles.protocolCell}>
                                                            {complaint.protocoloSac ? (
                                                                <span className={styles.protocolBadge}>{formatProtocol(complaint.protocoloSac)}</span>
                                                            ) : (
                                                                <span className={styles.noProtocol}>-</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className={styles.dateCell}>
                                                            {formatDateSafely(complaint.dataAbertura)}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className={styles.clientCell}>
                                                            <span className={styles.clientName}>{complaint.clienteNome}</span>
                                                            {complaint.segmento && (
                                                                <span className={styles.clientSegment}>{complaint.segmento}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={styles.orderCode}>{complaint.pedidoCodigo || complaint.vendaId}</span>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 500 }}>
                                                            {complaint.qcAutorizado ? complaint.qcAutorizado.split(',').map(a => a.trim()).map(a => a === 'Autorizado' ? 'Reposição' : a).join(', ') : (
                                                                <span className={styles.noOpinion}>-</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td
                                                        onClick={(e) => {
                                                            if (complaint.situacao === 'PENDENTE') {
                                                                e.stopPropagation();
                                                                handleStatusCellClick(complaint.id);
                                                            }
                                                        }}
                                                    >
                                                        <span className={`${styles.statusBadge} ${getStatusClass(complaint.situacao)}`}>
                                                            <span className={styles.statusDot}></span>
                                                            {getStatusLabel(complaint.situacao)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>

                                {/* Pagination Controls */}
                                <div className={styles.pagination}>
                                        <div className={styles.paginationInfo}>
                                            Página {currentPage} de {totalPages || 1} - Mostrando {displayComplaints.length} de {totalItems} reclamações
                                        </div>
                                        {totalPages > 1 && (
                                            <div className={styles.pageButtons}>
                                                <button
                                                    className={styles.pageButton}
                                                    onClick={() => {
                                                        setCurrentPage(prev => Math.max(1, prev - 1));
                                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                                    }}
                                                    disabled={currentPage === 1}
                                                >
                                                    <ChevronLeftIcon fontSize="small" />
                                                    Anterior
                                                </button>

                                                <div className={styles.pageNumbers}>
                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                                                        // Mostrar apenas páginas próximas à atual (Lógica do AdminUsersPage)
                                                        if (
                                                            page === 1 ||
                                                            page === totalPages ||
                                                            (page >= currentPage - 2 && page <= currentPage + 2)
                                                        ) {
                                                            return (
                                                                <button
                                                                    key={page}
                                                                    onClick={() => {
                                                                        setCurrentPage(page);
                                                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                    }}
                                                                    className={`${styles.pageNumber} ${page === currentPage ? styles.pageNumberActive : ''}`}
                                                                >
                                                                    {page}
                                                                </button>
                                                            );
                                                        } else if (page === currentPage - 3 || page === currentPage + 3) {
                                                            return <span key={page} className={styles.pageEllipsis}>...</span>;
                                                        }
                                                        return null;
                                                    })}
                                                </div>

                                                <button
                                                    className={styles.pageButton}
                                                    onClick={() => {
                                                        setCurrentPage(prev => Math.min(totalPages, prev + 1));
                                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                                    }}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    Próxima
                                                    <ChevronRightIcon fontSize="small" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                                )}
                            </div>
                        </>
                    )}

            <NewComplaintModal isOpen={isNewModalOpen} onClose={handleCloseNewModal} />
            {selectedOrderId && (
                <OrderDetailModal
                    order={{ ID: selectedOrderId } as any}
                    onClose={handleCloseOrderModal}
                />
            )}
        </div>
    );
};

export default ComplaintsPage;
